import { useMemo } from 'react';
import { useTranslation } from 'next-i18next';

import { type CategoryTree } from '@/shared/category/type';
import { type SchoolType } from '@/views/build/type';
import { getGradeNumber } from '@/views/build/util';

import { type PublisherList } from '../types';
import {
  convertPublisherInfoToCategoryNode,
  getPublisherNoneNode,
} from '../utils';

/**
 *
 * @param publishers - API 주입 받은 출판사 목록
 * @param selectedSchoolTypeId - 선택된 학교 타입 ID
 * @param selectedGradeId - 선택된 학년 ID
 * @param schoolInfoTree - 학교 정보 트리
 * @returns 출판사 노드 목록
 */
export const usePublisherNodeList = ({
  publishers,
  selectedSchoolTypeId,
  selectedGradeId,
  schoolInfoTree,
}: {
  publishers?: PublisherList;
  selectedSchoolTypeId?: string;
  selectedGradeId?: string;
  schoolInfoTree: CategoryTree;
}) => {
  const { t } = useTranslation(['build']);
  // 학년 번호 계산 (출판사 필터링용)
  const gradeNumber = useMemo(
    () => getGradeNumber(selectedGradeId),
    [selectedGradeId],
  );

  const selectedSchoolTypeName = useMemo(() => {
    return schoolInfoTree.children
      .find(d => d.categoryGroupId === selectedSchoolTypeId)
      ?.name?.toLowerCase() as SchoolType;
  }, [selectedSchoolTypeId, schoolInfoTree]);

  // 학교급에 따른 학년 오프셋 계산
  const selectedGrade = useMemo(() => {
    let gradeOffset = 0;
    if (selectedSchoolTypeName === 'middle') {
      gradeOffset = 6;
    }
    if (selectedSchoolTypeName === 'high') {
      gradeOffset = 9;
    }
    return gradeNumber + gradeOffset;
  }, [selectedSchoolTypeName, gradeNumber]);

  return useMemo(() => {
    return [
      getPublisherNoneNode(t),
      ...(publishers || [])
        .filter(
          publisher =>
            publisher.showSchoolTypes?.includes(
              selectedSchoolTypeName as SchoolType,
            ) &&
            !publisher.hideGrades.includes(
              selectedGrade !== 0 && !Number.isNaN(selectedGrade)
                ? selectedGrade
                : -1,
            ),
        )
        .map(convertPublisherInfoToCategoryNode),
    ];
  }, [t, publishers, selectedSchoolTypeName, selectedGrade]);
};
